/**
 * English — the translation.
 *
 * Typed as `Record<keyof typeof tr, string>`, so a key added to the Turkish
 * catalogue without an entry here will not compile. That ordering is the point:
 * Turkish is written first and English follows it, which is what stops English
 * phrasing leaking back into the Turkish page through literal translation.
 *
 * The Turkish is formal *siz* throughout, which English does not distinguish.
 * What carries the same register here is the *addressee*: this copy speaks to an
 * organisation evaluating a purchase, not to the operator sitting in the console
 * — so "your machines" rather than "your laptop", and no imperative that assumes
 * the reader is the person who will type the command.
 *
 * Postal addresses and telephone numbers are not here. They live in
 * `data/links.ts`, because a street does not change language.
 */
import type { tr } from './tr'

export const en: Record<keyof typeof tr, string> = {
  /* ---- Document ------------------------------------------------------ */
  'meta.title': 'Kimene — a control plane for an AI workforce',
  'meta.description':
    'You hold the pools: accounts, models, hosts, projects. Kimene decides which of them runs the work, records why, and executes it on your own machines through the real provider CLIs.',
  'meta.ogAlt': 'Kimene — the routing diagram, from pools to a recorded decision',

  'meta.product.title': 'Product — Kimene',
  'meta.product.description':
    'Pools, the routing decision, execution on real machines, the evidence contract, the approval gate, session continuity and fleet visibility. Every section names the command that proves its claim.',
  'meta.security.title': 'Security and architecture — Kimene',
  'meta.security.description':
    'The control plane holds no credential — only paths and references. Where the trust boundary runs, what is recorded, and the gaps the installation is honest about.',
  'meta.company.title': 'Company — Kimene',
  'meta.company.description':
    'Kimene is built by Arpanet Bilişim Teknolojileri A.Ş.; the team works at the METU Technopark in Ankara. The three rules the product is built on, and the registered details.',
  'meta.contact.title': 'Contact — Kimene',
  'meta.contact.description':
    'Talk to us directly about a demo, installation or pricing. A control plane built at the METU Technopark in Ankara. Telephone: {phone}',

  /* ---- Chrome -------------------------------------------------------- */
  'nav.skip': 'Skip to content',
  'nav.home': 'Home',
  'nav.product': 'Product',
  'nav.security': 'Security',
  'nav.company': 'Company',
  'nav.contact': 'Contact',
  'nav.console': 'Open the console',
  'nav.locale': 'Language',
  'nav.localeOther': 'Türkçe',
  'nav.localeSwitch': 'Read this page in {language}',
  'nav.menuOpen': 'Open the menu',
  'nav.menuClose': 'Close the menu',
  'nav.site': 'Site navigation',
  'nav.themeToLight': 'Switch to the light theme',
  'nav.themeToDark': 'Switch to the dark theme',

  'ui.live': 'live',
  'ui.waiting': 'waiting',
  'ui.done': 'done',
  'ui.unmeasured': 'unmeasured',
  'ui.immutable': 'immutable',
  'ui.selected': 'selected',
  'ui.rejected': 'rejected',
  'ui.total': 'total',
  'ui.components': 'components',
  'ui.penalties': 'penalties',
  'ui.weight': 'weight',
  'ui.approve': 'Approve',
  'ui.reject': 'Reject',
  'ui.real': 'real',
  'ui.simulated': 'simulated',
  'ui.behind': 'behind',
  'ui.proof': 'proof',
  'ui.onThisPage': 'On this page',
  'ui.readMore': 'More',

  /* ---- 1 · Hero ------------------------------------------------------ */
  'hero.eyebrow': 'A control plane that runs on your own machines',
  'hero.title': 'Your AI workforce.',
  'hero.deck': 'One route, one decision, one record.',
  'hero.sub':
    'You hold the pools: accounts, models, hosts, projects, roles. Kimene decides which of them runs the work, writes down why, and executes it on real machines through the real CLIs.',
  'hero.ctaPrimary': 'Request a demo',
  'hero.ctaSecondary': 'Open the console',
  'hero.fig.pools': 'Pools',
  'hero.fig.eligibility': 'Eligibility',
  'hero.fig.weights': 'Weights',
  'hero.fig.decision': 'Decision',
  'hero.fig.route': 'Route',
  'hero.fig.record': 'Record',
  'hero.fig.alt':
    'Candidates from five pools pass through an eligibility gate; seven are rejected with a reason, the survivors are scored against weights, and a single decision node produces a route and an immutable record.',

  /* ---- 2 · Pools ----------------------------------------------------- */
  'pools.eyebrow': 'Pools',
  'pools.title': 'No entity owns another.',
  'pools.sub':
    'No account pinned to a project, no model pinned to an account. Association happens at execution time, is recorded as a route valid for that one run, and dissolves when the work ends.',
  'pools.body.1':
    'A project expresses preferences, permissions and a routing mode. The router combines those with live capacity, health and history. What comes out is a record, not a configuration.',
  'pools.pull': 'Kimene does not invent a quota.',
  'pools.body.2':
    'Provider capacity, concurrency policy, resource leases and priority are four different things. Compressed into the one word "quota", an operator can never again tell which limit they hit.',
  'pools.proof': 'go test ./internal/domain — no quota field exists in the domain',
  'pools.fig.forbidden': 'never',
  'pools.fig.run1': 'first run',
  'pools.fig.run2': 'second run',
  'pools.fig.alt':
    'Five pools side by side with no permanent edge between them. Two tasks from the same project route to two different accounts, each writing its own execution record.',

  /* ---- 3 · The decision record --------------------------------------- */
  'decision.eyebrow': 'The decision record',
  'decision.title': '"The AI chose it" is not an explanation.',
  'decision.sub':
    'Hard eligibility first, then weighted scoring. The rejected candidates are written into the immutable decision with their reasons, exactly as the winner is.',
  'decision.body.1':
    'The record carries the selected route, the candidates considered and rejected, every rejection reason, every component of the score, the capacity snapshots consulted, and any operator override.',
  'decision.pull': 'total = Σ components − Σ penalties. There is no third path.',
  'decision.body.2':
    'The route simulator replays the same scoring with hypothetical inputs, acquiring no leases and starting no sessions. "What if that host were down" has an answer that is arithmetic, not a guess.',
  'decision.proof': 'go test ./internal/routing — refuses a decision that cannot explain itself',
  'decision.fig.title': 'Routing decision',
  'decision.fig.why': 'Why this account?',
  'decision.fig.toggle': 'Mark the host offline',
  'decision.fig.alt':
    'A routing record: six score components, four penalties, the total, and seven candidates rejected with their reasons.',

  /* ---- 4 · Execution ------------------------------------------------- */
  'exec.eyebrow': 'Execution',
  'exec.title': 'The work runs on real machines, in the real CLIs.',
  'exec.sub':
    'The control plane never speaks to a provider CLI directly. Every session lives inside a tmux session started by the node agent on that machine.',
  'exec.body.1':
    'That indirection is what makes a browser refresh, a server deploy and a dropped SSH connection harmless to work in progress.',
  'exec.durability': 'Close the browser. Deploy the server. Drop the SSH. The session stands.',
  'exec.body.2':
    'On reconnect the screen is not redrawn from scratch; it resumes from the cursor you left. Every keystroke you send is applied exactly once — never swallowed, never typed twice.',
  'exec.proof':
    'make test-live-terminal — a running loop survives its manager being shut down entirely',
  'exec.fig.browserClosed': 'browser closed',
  'exec.fig.serverDeployed': 'server restarted',
  'exec.fig.sshDropped': 'SSH dropped',
  'exec.fig.alt':
    'Three terminal tiles: one typing, one waiting for input, one finished. Beneath them an unbroken session line crossed by three interruption marks.',

  /* ---- 5 · Evidence -------------------------------------------------- */
  'evidence.eyebrow': 'Evidence',
  'evidence.title': '"Done" is not a thing the agent gets to say.',
  'evidence.sub':
    'Completed is reachable only through review or awaiting approval, and one transition table enforces it. There is no path in the API that writes a task state directly.',
  'evidence.body.1':
    'Each kind of task carries its own evidence contract. A code task wants a diff, a test, a lint and a build report plus a reviewer verdict; a research task wants sources with access dates.',
  'evidence.pull': 'Kimene will not invent a test command on your behalf.',
  'evidence.body.2':
    'A gate you have not defined stays shut and says why it is shut. Every report carries the command that produced it; artifacts are immutable, content-addressed and hash-verified.',
  'evidence.proof':
    'go test ./internal/domain ./internal/artifact — completed is reachable only from review or approval',
  'evidence.fig.contract': 'Evidence contract',
  'evidence.fig.missing': 'no command defined',
  'evidence.fig.define': 'Define the command',
  'evidence.fig.locked': 'transition closed',
  'evidence.fig.alt':
    'Four evidence cards: diff, test and build pass; the lint card is missing because no command is defined. The completion transition stays shut.',

  /* ---- 6 · Approval -------------------------------------------------- */
  'approval.eyebrow': 'Approval',
  'approval.title': 'A person signs what a person must sign.',
  'approval.sub':
    'Risk level comes from code, not from a prompt. High-risk work goes to a person, and if the operator requires it, that approval asks for a second factor.',
  'approval.body.1':
    'The approval card says what was asked for, which rule fired, which resources it touches and what happens if it is refused. The decision is recorded with who made it.',
  'approval.brakeTitle': 'Unattended, the chief only brakes.',
  'approval.brakeSub':
    'What may be applied with nobody watching are the actions that stop work and call for a person. Nothing that starts work or spends a subscription is applied without a human.',
  'approval.brakeCan': 'may run unattended',
  'approval.brakeCannot': 'waits for a person',
  'approval.proof':
    'go test ./internal/approval — approving your own work is structurally impossible',
  'approval.fig.alt':
    'An approval card beside a six-digit second-factor field; below them, the list of actions that may run unattended and those that wait for a person.',

  /* ---- 7 · Continuity ------------------------------------------------ */
  'continuity.eyebrow': 'Continuity',
  'continuity.title': 'A holder that arrives late cannot apply an effect.',
  'continuity.sub':
    'Every lease carries an increasing fencing token. The guarded resource remembers the highest token it has seen and refuses anything lower.',
  'continuity.body.1':
    'Leases live somewhere explicitly allowed to lose data. The only reason that pairing is safe is the fence: Kimene does not claim a guarantee a single cache cannot give — it claims the weaker, true one.',
  'continuity.boundaryTitle': 'A switch happens only at a safe boundary.',
  'continuity.boundarySub':
    'A finished turn, a written checkpoint, a wait for a person, or a confirmed rate limit. Never mid tool-call, mid-write, mid-migration or mid-deploy.',
  'continuity.pull': 'Transient is not persistent.',
  'continuity.body.2':
    'A two-second blip is not a provider failure; it retries in place. Only a typed, confirmed rate limit, auth failure or unreachable host moves the route.',
  'continuity.proof':
    'make test-integration-live — 32-way contention yields exactly one winner; a dead holder’s renewal is refused',
  'continuity.fig.refused': 'refused',
  'continuity.fig.alt':
    'A timeline: the holder that took the lease with token 17 pauses, the lease expires, a second holder writes with 18, and the first one’s late write is refused.',

  /* ---- 8 · Fleet ----------------------------------------------------- */
  'fleet.eyebrow': 'Fleet',
  'fleet.title': 'Unmeasured is not zero.',
  'fleet.sub':
    'Unmeasured capacity has its own colour and its own hatching. Kimene never draws it as a number, because an empty bar and a zero bar look identical and mean opposite things.',
  'fleet.body.1':
    'The same refusal holds in scoring: unknown capacity is not an optimistic 1.0. A record, a measurement and an estimate are kept apart.',
  'fleet.degradationTitle': 'What happens when a dependency goes down is written out.',
  'fleet.degradationSub': 'No row in that table says "show stale data as though it were fresh".',
  'fleet.proof':
    'go test ./internal/domain — capacity is a pointer, so unknown is representable and cannot read as zero',
  'fleet.fig.cpu': 'CPU',
  'fleet.fig.ram': 'Memory',
  'fleet.fig.disk': 'Disk',
  'fleet.fig.gpu': 'GPU',
  'fleet.fig.alt':
    'Four host rows; three have filled meters, one has a hatched GPU meter reading "unmeasured".',

  /* ---- 9 · Security -------------------------------------------------- */
  'security.eyebrow': 'Security',
  'security.title': 'The control plane holds no credentials.',
  'security.sub':
    'Credentials stay on the machine that owns them; the database knows only a path and a reference. Compromising the database yields no provider credential.',
  'security.body.1':
    'Provider isolation goes through the process environment, never a command line: a process list is world-readable and an environment is not. Nodes speak mutual TLS after a single-use enrolment token.',
  'security.pull': 'Being on an internal network is not a security control.',
  'security.body.2':
    'Redaction lives inside the log handler rather than at call sites, so it cannot be forgotten. Terminal output is never written as raw HTML, and a browser never connects to a machine directly.',
  'security.point.mtls': 'Mutual TLS for every byte after enrolment',
  'security.point.hostLocal': 'Credentials stay on the machine that owns them',
  'security.point.redaction': 'Redaction cannot be bypassed',
  'security.point.proxied': 'The browser never reaches a host directly',
  'security.proof': 'go test ./internal/security/redact — redaction lives in the log handler',
  'security.fig.alt':
    'Three zones: browser, control plane and host. The credential never leaves the host; a log line crossing the boundary has its secret removed as it crosses.',

  /* ---- 10 · Install -------------------------------------------------- */
  'install.eyebrow': 'Install',
  'install.title': 'Four dependencies, four binaries, one doctor.',
  'install.sub':
    'Ubuntu, Go and Node. A database, a queue, a cache and a message broker. doctor verifies all of them without changing anything, and tells you which mode it found.',
  'install.body.1':
    'The recovery procedure differs by mode, which is why doctor names it. No containers, no orchestrator, no runtime that must be installed on every target before anything can run.',
  'install.firstRun':
    'One step waits for you on a fresh install: no account/model pairing is routable until one has been verified. That is deliberate — it is what stops work being sent to a pairing nobody has checked.',
  'install.gapsTitle': 'Known gaps.',
  'install.gapsSub':
    'Every document carries a "known gaps" section. That is the honest part; read it before relying on a capability.',
  'install.gap.backup': 'No scheduled backup — the commands exist, nothing runs them on a timer.',
  'install.gap.nodeBootstrap':
    'No one-command node install; the binary is copied and enrolled by hand.',
  'install.gap.tls':
    'The server install script does nothing about TLS or the reverse proxy; both are manual.',
  'install.gap.retention': 'A retention policy is seeded, and nothing enforces it.',
  'install.gap.retry':
    'A handler that fails for a transient reason is not retried; it dead-letters.',
  'install.gap.terminal': 'The terminal layer has not yet been exercised against a live tmux.',
  'install.proof': 'make doctor — verifies all four dependencies non-destructively',

  /* Degradation matrix — verbatim from docs/ARCHITECTURE.md §14. */
  'fleet.degr.mongodb':
    'Writes stop; reads are not served from cache as if fresh; an explicit unavailable state.',
  'fleet.degr.rabbitmq':
    'UI and state stay readable; new async work queues in the local outbox; health shows degraded.',
  'fleet.degr.redis':
    'Distributed operations are restricted; critical leases fail closed; an explicit warning.',
  'fleet.degr.mqtt':
    'Node heartbeat falls back to the control-plane WebSocket; telemetry is badged degraded.',

  /* Safe boundary — the right-hand column. Described, because the code
     enumerates no such set: unsafe is everything that is not a boundary. */
  'continuity.unsafe.tool': 'mid tool call',
  'continuity.unsafe.write': 'mid file write',
  'continuity.unsafe.migration': 'mid migration',
  'continuity.unsafe.deploy': 'mid deploy',
  'continuity.unsafe.paste': 'mid paste',
  /* ---- Home: the four pillars ---------------------------------------- */
  'home.pillars.eyebrow': 'What it does',
  'home.pillars.title': 'Four things, one plane.',
  'home.pillars.sub':
    'Kimene holds the pools, decides which of them runs a piece of work, executes it on real machines, and writes down what happened with the evidence attached. Each has a section of its own, one level deeper, with the command that proves it.',

  'home.pillar.pools.title': 'The pools stay yours',
  'home.pillar.pools.body':
    'Accounts, models, hosts, projects, roles and tools are separate pools. None of them owns another; the association is made only while work runs, and it is recorded.',
  'home.pillar.decision.title': 'The decision is written down',
  'home.pillar.decision.body':
    'Which account, which model and which host were chosen — and which were rejected, and why. The components of the score stand as an immutable record.',
  'home.pillar.evidence.title': 'Completion requires evidence',
  'home.pillar.evidence.body':
    'A task reaches completion only through review or approval. The evidence contract comes from the kind of task it is; the API has no path to write the state directly.',
  'home.pillar.boundary.title': 'Credentials stay at the boundary',
  'home.pillar.boundary.body':
    'The control plane stores no credential body and carries none between machines. It knows paths and references; provider isolation is established through the process environment.',

  /* ---- Home: the showcase -------------------------------------------- */
  'home.showcase.eyebrow': 'The part that is real',
  'home.showcase.title': 'Not an abstraction: live sessions on real machines.',
  'home.showcase.sub':
    'Every session runs on a host, through the provider’s own command-line tool. The wall shows them side by side, and an operator can take the keyboard at any moment.',
  'home.showcase.link': 'Read how execution works',

  /* ---- Home: the company strip --------------------------------------- */
  'home.company.eyebrow': 'Built by',
  'home.company.sub':
    'Kimene is built and supported by an engineering team at the METU Technopark in Ankara.',
  'home.company.link': 'Company details',

  /* ---- Product page --------------------------------------------------- */
  'product.eyebrow': 'Product',
  'product.title': 'Seven sections, seven proofs.',
  'product.sub':
    'Each section below makes one claim and names the command that proves it. The order is the path a piece of work takes through Kimene: from the pools to a decision, from the decision to execution, and from execution to evidence.',

  /* ---- Security page -------------------------------------------------- */
  'securityPage.eyebrow': 'Security and architecture',
  'securityPage.title': 'What you hand over, and what you do not.',
  'securityPage.sub':
    'This page answers two questions: what the control plane can reach, and what happens when part of it fails. Installation is here too, because “it runs on your machines” is a security fact before it is a getting-started one.',

  /* ---- Company page --------------------------------------------------- */
  'companyPage.eyebrow': 'Company',
  'companyPage.title': 'Who builds Kimene.',
  'companyPage.sub':
    'Software that runs on your own servers and manages your provider accounts is bought from somebody. This is who.',

  'company.who.body.1':
    'Kimene is built and supported by an engineering team at the METU Informatics Innovation Center in Ankara. The product is installed on your own infrastructure: the data stays on your servers, and the provider accounts are your accounts.',
  'company.who.pull': 'You run the software; we install it, adapt it and support it.',
  'company.who.body.2':
    'Get in touch directly for installation, integration and upgrades. Kimene is a closed-source product; licensing and support terms are agreed in conversation.',

  'company.fact.legal': 'Registered name',
  'company.fact.address': 'Address',
  'company.fact.email': 'Email',
  'company.fact.phone': 'Telephone',

  'company.principles.eyebrow': 'The product’s rules',
  'company.principles.title': 'Three rules, all three in the code.',
  'company.principles.sub':
    'These are not slogans but constraints the software has to satisfy. All three can be checked on the product page, each with the command that proves it.',
  'company.p1.title': 'No pool owns another',
  'company.p1.body':
    'There is no account pinned to a project, no model pinned to an account. The association is made only while work runs, and written as an immutable routing decision.',
  'company.p2.title': 'Unknown is not zero',
  'company.p2.body':
    'When capacity cannot be measured, no number is invented. The unknown has its own colour, and “probably fine” never reaches the interface.',
  'company.p3.title': 'Completion requires evidence',
  'company.p3.body':
    'A task reaches completion only through review or approval. The transition table enforces it, and no shortcut can be added.',
  'company.principles.link': 'See all three on the product page',

  /* ---- Contact page --------------------------------------------------- */
  'contactPage.eyebrow': 'Contact',
  'contactPage.title': 'Let’s talk.',
  'contactPage.sub':
    'Reach us directly about installation, a demo or pricing. The telephone is the fastest answer.',

  'contact.reach.title': 'How to reach us',
  'contact.phone.label': 'Telephone',
  'contact.phone.note': 'Weekdays 09:00–18:00 (Istanbul time).',
  'contact.email.label': 'Email',
  'contact.email.note': 'Usually answered the same working day.',
  'contact.address.label': 'Address',
  'contact.address.note': 'Please arrange a visit in advance.',

  'contact.demo.eyebrow': 'Demo',
  'contact.demo.title': 'See it on your own scenario.',
  'contact.demo.sub':
    'We build the demo around your workflow. All we need to prepare is a rough description of the work you want to automate.',
  'contact.demo.item.1': 'What the pools would look like for you: accounts, models, hosts.',
  'contact.demo.item.2': 'One piece of work end to end: decision, execution, evidence, approval.',
  'contact.demo.item.3': 'The infrastructure installation and integration would need.',
  'contact.demo.subject': 'Kimene demo request',
  'contact.demo.cta': 'Send a demo request',
  'contact.demo.call': 'Call us',


  /* ---- CTA and footer ------------------------------------------------ */
  'cta.title': 'On your own machines, with your own accounts.',
  'cta.sub':
    'Kimene runs on your infrastructure and manages your own provider accounts. It is not a credential-sharing service, not a multi-tenant proxy, and not a way around provider limits.',
  'cta.primary': 'Get in touch',
  'cta.secondary': 'Call us',
  'foot.providerTerms':
    'Kimene manages the operator’s own trusted profiles. Complying with the providers’ terms of use is the operator’s responsibility.',
  'foot.builtWith': 'Built with Go, React and tmux.',
  'foot.siteHeading': 'Site',
  'foot.contactHeading': 'Contact',
  'foot.rights': 'All rights reserved.',
}
